import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface InterruptedRunBannerProps {
  recoverableFailedRun: { runId: string } | null;
  onContinue: () => Promise<void>;
}

/**
 * A run lifecycle notice, kept outside the virtualized message list because
 * it is not a persisted chat message.
 */
export function InterruptedRunBanner({
  recoverableFailedRun,
  onContinue,
}: InterruptedRunBannerProps) {
  const { t } = useTranslation();
  const [isContinuing, setIsContinuing] = useState(false);

  if (!recoverableFailedRun) return null;

  const handleContinue = async () => {
    setIsContinuing(true);
    try {
      await onContinue();
    } finally {
      setIsContinuing(false);
    }
  };

  return (
    <Alert
      aria-live="polite"
      className="flex shrink-0 items-center justify-between gap-3 rounded-none border-x-0 border-b-0 bg-muted/40 px-3 py-2"
      data-testid="interrupted-run-banner"
      role="status"
    >
      <AlertDescription className="text-muted-foreground">
        {t("common:interruptedRun.message")}
      </AlertDescription>
      <Button
        className="shrink-0"
        disabled={isContinuing}
        onClick={handleContinue}
        size="sm"
        variant="outline"
      >
        {t("common:interruptedRun.continue")}
      </Button>
    </Alert>
  );
}
